/**
 * Score → Letter grade mapping.
 *
 * Two scales because raw and post-debate scores live in different ranges
 * (recalibrated on prod 2026-05-06, n=99 niches with debates):
 *
 *   raw `score` (Seeker, pre-debate):  range 52..89, mean 81.8, σ ≈6
 *   `final_score` (after Destroyer):   range 52..66, mean 61.6, σ ≈2.9
 *
 * Discovery thresholds shifted up 1–2 points (S was being handed out to
 * 21% of niches under old calibration — now ~2%, A+ ~20%, median sits at
 * A-). Debate thresholds essentially unchanged: S 65→66 (65 is a dead
 * zone that never appears in data) and C+ floor 50→53 (52 was the only
 * "C+" and should read as plain C).
 *
 * Apply `getDebateGrade` to `final_score` (after the Seeker→Destroyer
 * exchange — what the user sees on the niche page, share-card, PDF).
 * Apply `getDiscoveryGrade` to raw `score` for surfaces that show only
 * the initial Seeker assessment (no debate yet).
 *
 * `getScoreGrade` is kept as a backwards-compatible alias of
 * `getDebateGrade` — every existing callsite already feeds it post-debate
 * (or `final_score` lifted into `score`). New callers should pick the
 * matching scale explicitly.
 */

export interface ScoreGrade {
  grade: string;
  label: string;
  color: "emerald" | "amber" | "red";
}

/** Debate scale — calibrated for `final_score` (post-Seeker-rebuttal). */
export function getDebateGrade(score: number): ScoreGrade {
  if (score >= 66) return { grade: "S", label: "Exceptional — rare opportunity", color: "emerald" };
  if (score >= 64) return { grade: "A+", label: "Excellent foundation", color: "emerald" };
  if (score >= 63) return { grade: "A", label: "Strong opportunity", color: "emerald" };
  if (score >= 62) return { grade: "A-", label: "Promising with clear caveats", color: "emerald" };
  if (score >= 60) return { grade: "B+", label: "Solid mid — needs sharpening", color: "amber" };
  if (score >= 58) return { grade: "B", label: "Viable with caveats", color: "amber" };
  if (score >= 56) return { grade: "B-", label: "Weak signal — revisit positioning", color: "amber" };
  if (score >= 53) return { grade: "C+", label: "Risky — significant rework", color: "red" };
  return { grade: "C", label: "Needs rethinking", color: "red" };
}

/** Discovery scale — calibrated for raw Seeker `score` (pre-debate). */
export function getDiscoveryGrade(score: number): ScoreGrade {
  if (score >= 89) return { grade: "S", label: "Exceptional — rare opportunity", color: "emerald" };
  if (score >= 87) return { grade: "A+", label: "Excellent foundation", color: "emerald" };
  if (score >= 85) return { grade: "A", label: "Strong opportunity", color: "emerald" };
  if (score >= 82) return { grade: "A-", label: "Promising with clear caveats", color: "emerald" };
  if (score >= 80) return { grade: "B+", label: "Solid mid — needs sharpening", color: "amber" };
  if (score >= 77) return { grade: "B", label: "Viable with caveats", color: "amber" };
  if (score >= 73) return { grade: "B-", label: "Weak signal — revisit positioning", color: "amber" };
  if (score >= 65) return { grade: "C+", label: "Risky — significant rework", color: "red" };
  return { grade: "C", label: "Needs rethinking", color: "red" };
}

/**
 * Backwards-compatible alias — points at the debate scale because every
 * existing callsite either reads `final_score` directly or lifts it into
 * `niche.score` before calling this (see share-card / PDF routes). Prefer
 * `getDebateGrade` / `getDiscoveryGrade` in new code.
 */
export const getScoreGrade = getDebateGrade;
